import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function Exams() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'available' | 'upcoming' | 'completed'>('available');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');

  const subjects = [
    { id: 'all', name: 'جميع المواد', icon: 'ri-apps-line' },
    { id: 'math', name: 'الرياضيات', icon: 'ri-calculator-line' },
    { id: 'physics', name: 'الفيزياء', icon: 'ri-atom-line' },
    { id: 'chemistry', name: 'الكيمياء', icon: 'ri-flask-line' },
    { id: 'biology', name: 'الأحياء', icon: 'ri-microscope-line' },
    { id: 'arabic', name: 'اللغة العربية', icon: 'ri-book-open-line' },
    { id: 'english', name: 'اللغة الإنجليزية', icon: 'ri-global-line' }
  ];

  const availableExams = [
    {
      id: 1,
      course: 'الرياضيات المتقدمة',
      subject: 'math',
      duration: 60,
      questions: 30,
      type: 'اختيار من متعدد',
      passingScore: 60,
      totalScore: 100,
      icon: 'ri-calculator-line',
      color: 'bg-blue-500',
      instructor: 'د. محمد أحمد',
      deadline: '2024-02-15',
      difficulty: 'متوسط',
      attempts: 0,
      maxAttempts: 3
    },
    {
      id: 2,
      course: 'الفيزياء الحديثة',
      subject: 'physics',
      duration: 90,
      questions: 40,
      type: 'متنوع',
      passingScore: 65,
      totalScore: 100,
      icon: 'ri-atom-line',
      color: 'bg-purple-500',
      instructor: 'د. سارة علي',
      deadline: '2024-02-18',
      difficulty: 'صعب',
      attempts: 1,
      maxAttempts: 2
    },
    {
      id: 3,
      course: 'الكيمياء العضوية',
      subject: 'chemistry',
      duration: 75,
      questions: 35,
      type: 'اختيار من متعدد',
      passingScore: 60,
      totalScore: 100,
      icon: 'ri-flask-line',
      color: 'bg-green-500',
      instructor: 'د. أحمد حسن',
      deadline: '2024-02-20',
      difficulty: 'متوسط',
      attempts: 0,
      maxAttempts: 3
    },
    {
      id: 4,
      course: 'علم الأحياء الجزيئي',
      subject: 'biology',
      duration: 60,
      questions: 25,
      type: 'صح وخطأ',
      passingScore: 70,
      totalScore: 100,
      icon: 'ri-microscope-line',
      color: 'bg-teal-500',
      instructor: 'د. فاطمة محمود',
      deadline: '2024-02-22',
      difficulty: 'سهل',
      attempts: 0,
      maxAttempts: 3
    }
  ];

  const upcomingExams = [
    {
      id: 5,
      course: 'النحو والصرف',
      subject: 'arabic',
      duration: 45,
      questions: 20,
      type: 'اختيار من متعدد',
      passingScore: 60,
      totalScore: 100,
      icon: 'ri-book-open-line',
      color: 'bg-orange-500',
      instructor: 'د. خالد عبدالله',
      startDate: '2024-02-25',
      difficulty: 'متوسط'
    },
    {
      id: 6,
      course: 'قواعد اللغة الإنجليزية',
      subject: 'english',
      duration: 50,
      questions: 30,
      type: 'متنوع',
      passingScore: 65,
      totalScore: 100,
      icon: 'ri-global-line',
      color: 'bg-indigo-500',
      instructor: 'د. ليلى إبراهيم',
      startDate: '2024-02-28',
      difficulty: 'سهل'
    }
  ];

  const completedExams = [
    {
      id: 7,
      course: 'الجبر الخطي',
      subject: 'math',
      score: 85,
      totalScore: 100,
      percentage: 85,
      status: 'ناجح',
      icon: 'ri-calculator-line',
      color: 'bg-blue-500',
      completedDate: '2024-01-20',
      attempts: 2
    },
    {
      id: 8,
      course: 'الميكانيكا الكلاسيكية',
      subject: 'physics',
      score: 72,
      totalScore: 100,
      percentage: 72,
      status: 'ناجح',
      icon: 'ri-atom-line',
      color: 'bg-purple-500',
      completedDate: '2024-01-15',
      attempts: 1
    },
    {
      id: 9,
      course: 'الكيمياء التحليلية',
      subject: 'chemistry',
      score: 55,
      totalScore: 100,
      percentage: 55,
      status: 'راسب',
      icon: 'ri-flask-line',
      color: 'bg-green-500',
      completedDate: '2024-01-10',
      attempts: 3
    }
  ];

  const filteredExams = (exams: any[]) => {
    return exams.filter(exam => {
      const matchesSearch = exam.course.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSubject = selectedSubject === 'all' || exam.subject === selectedSubject;
      return matchesSearch && matchesSubject;
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'سهل': return 'bg-green-100 text-green-700';
      case 'متوسط': return 'bg-yellow-100 text-yellow-700';
      case 'صعب': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'ناجح' ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#E8F5F3]">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F2A3A] mb-3">الاختبارات</h1>
          <p className="text-slate-600 text-lg">إدارة ومتابعة جميع اختباراتك الدراسية</p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Search */}
            <div className="relative">
              <i className="ri-search-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl"></i>
              <input
                type="text"
                placeholder="ابحث عن اختبار..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-slate-200 rounded-xl focus:border-[#1ABC9C] focus:outline-none text-sm"
              />
            </div>

            {/* Subject Filter */}
            <div className="relative">
              <i className="ri-filter-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl"></i>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-slate-200 rounded-xl focus:border-[#1ABC9C] focus:outline-none text-sm cursor-pointer appearance-none bg-white"
              >
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl pointer-events-none"></i>
            </div>
          </div>

          {/* Subject Pills */}
          <div className="flex flex-wrap gap-3">
            {subjects.map(subject => (
              <button
                key={subject.id}
                onClick={() => setSelectedSubject(subject.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedSubject === subject.id
                    ? 'bg-[#1ABC9C] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <i className={`${subject.icon} text-lg`}></i>
                <span className="text-sm">{subject.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-2 mb-8 inline-flex gap-2">
          <button
            onClick={() => setActiveTab('available')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'available'
                ? 'bg-[#1ABC9C] text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <i className="ri-file-list-3-line ml-2"></i>
            المتاحة ({filteredExams(availableExams).length})
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'upcoming'
                ? 'bg-[#1ABC9C] text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <i className="ri-calendar-line ml-2"></i>
            القادمة ({filteredExams(upcomingExams).length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'completed'
                ? 'bg-[#1ABC9C] text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <i className="ri-checkbox-circle-line ml-2"></i>
            المكتملة ({filteredExams(completedExams).length})
          </button>
        </div>

        {/* Available Exams */}
        {activeTab === 'available' && (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredExams(availableExams).map(exam => (
              <div key={exam.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className={`${exam.color} p-6 text-white`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                        <i className={`${exam.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{exam.course}</h3>
                        <p className="text-white/80 text-sm">{exam.instructor}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(exam.difficulty)} bg-white/90`}>
                      {exam.difficulty}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <i className="ri-time-line"></i>
                      <span>{exam.duration} دقيقة</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-question-line"></i>
                      <span>{exam.questions} سؤال</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xs text-slate-600 mb-1">نوع الاختبار</p>
                      <p className="font-bold text-[#0F2A3A] text-sm">{exam.type}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xs text-slate-600 mb-1">درجة النجاح</p>
                      <p className="font-bold text-[#0F2A3A] text-sm">{exam.passingScore}%</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xs text-slate-600 mb-1">آخر موعد</p>
                      <p className="font-bold text-[#0F2A3A] text-sm">{exam.deadline}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xs text-slate-600 mb-1">المحاولات</p>
                      <p className="font-bold text-[#0F2A3A] text-sm">{exam.attempts}/{exam.maxAttempts}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/exam-details/${exam.id}`)}
                    className="w-full py-3 bg-[#1ABC9C] text-white font-semibold rounded-xl hover:bg-[#16a085] transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line ml-2"></i>
                    ابدأ الاختبار
                  </button>
                </div>
              </div>
            ))}

            {filteredExams(availableExams).length === 0 && (
              <div className="col-span-2 text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-slate-100 rounded-full">
                  <i className="ri-file-list-3-line text-5xl text-slate-400"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-700 mb-2">لا توجد اختبارات متاحة</h3>
                <p className="text-slate-500">جرب تغيير معايير البحث أو الفلترة</p>
              </div>
            )}
          </div>
        )}

        {/* Upcoming Exams */}
        {activeTab === 'upcoming' && (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredExams(upcomingExams).map(exam => (
              <div key={exam.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className={`${exam.color} p-6 text-white`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                        <i className={`${exam.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{exam.course}</h3>
                        <p className="text-white/80 text-sm">{exam.instructor}</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-semibold">
                      قريباً
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl mb-4">
                    <i className="ri-calendar-event-line text-2xl text-blue-600"></i>
                    <div>
                      <p className="text-xs text-slate-600">يبدأ في</p>
                      <p className="font-bold text-[#0F2A3A]">{exam.startDate}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-3 bg-slate-50 rounded-xl">
                      <i className="ri-time-line text-xl text-[#1ABC9C] mb-1"></i>
                      <p className="text-xs text-slate-600">{exam.duration} دقيقة</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-xl">
                      <i className="ri-question-line text-xl text-[#1ABC9C] mb-1"></i>
                      <p className="text-xs text-slate-600">{exam.questions} سؤال</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-xl">
                      <i className="ri-percent-line text-xl text-[#1ABC9C] mb-1"></i>
                      <p className="text-xs text-slate-600">{exam.passingScore}%</p>
                    </div>
                  </div>

                  <button
                    disabled
                    className="w-full py-3 bg-slate-200 text-slate-500 font-semibold rounded-xl cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-lock-line ml-2"></i>
                    غير متاح بعد
                  </button>
                </div>
              </div>
            ))}

            {filteredExams(upcomingExams).length === 0 && (
              <div className="col-span-2 text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-slate-100 rounded-full">
                  <i className="ri-calendar-line text-5xl text-slate-400"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-700 mb-2">لا توجد اختبارات قادمة</h3>
                <p className="text-slate-500">سيتم إضافة الاختبارات الجديدة قريباً</p>
              </div>
            )}
          </div>
        )}

        {/* Completed Exams */}
        {activeTab === 'completed' && (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredExams(completedExams).map(exam => (
              <div key={exam.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className={`${exam.color} p-6 text-white`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                        <i className={`${exam.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{exam.course}</h3>
                        <p className="text-white/80 text-sm">مكتمل في {exam.completedDate}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-center flex-1">
                      <div className="relative inline-block mb-2">
                        <svg className="w-24 h-24" viewBox="0 0 100 100">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#E5E7EB"
                            strokeWidth="8"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke={exam.percentage >= 60 ? '#1ABC9C' : '#EF4444'}
                            strokeWidth="8"
                            strokeDasharray={`${(exam.percentage / 100) * 251.2} 251.2`}
                            strokeLinecap="round"
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className={`text-2xl font-bold ${exam.percentage >= 60 ? 'text-[#1ABC9C]' : 'text-red-600'}`}>
                            {exam.percentage}%
                          </span>
                        </div>
                      </div>
                      <p className={`font-bold text-lg ${getStatusColor(exam.status)}`}>{exam.status}</p>
                    </div>

                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <span className="text-sm text-slate-600">الدرجة</span>
                        <span className="font-bold text-[#0F2A3A]">{exam.score}/{exam.totalScore}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <span className="text-sm text-slate-600">المحاولات</span>
                        <span className="font-bold text-[#0F2A3A]">{exam.attempts}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/exam-result/${exam.id}`)}
                    className="w-full py-3 bg-[#0F2A3A] text-white font-semibold rounded-xl hover:bg-[#1a3d52] transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-text-line ml-2"></i>
                    عرض التقرير التفصيلي
                  </button>
                </div>
              </div>
            ))}

            {filteredExams(completedExams).length === 0 && (
              <div className="col-span-2 text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-slate-100 rounded-full">
                  <i className="ri-checkbox-circle-line text-5xl text-slate-400"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-700 mb-2">لا توجد اختبارات مكتملة</h3>
                <p className="text-slate-500">ابدأ اختبارك الأول الآن</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
